import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth';
import { useUserStore } from '@/store/userStore';
import { isPaidPlan } from '@/utils/planHelpers';
import { useSettingsStore, LANGUAGES, APP_VERSION, type Language } from '@/store/settingsStore';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { PRO_PLAN, MAX_PLAN } from '@/api/config';
import { apiService } from '@/api';
import {
  Crown, LogOut, Globe,
  Share2, HelpCircle, FileText, Shield, ChevronDown,
  Bug, Instagram, Linkedin, Youtube, Facebook,
  Clock, Briefcase, Star, Info, Package,
  X, XCircle, DollarSign, ArrowLeft, Mail, Settings,
  AlertTriangle, Film, Download, Sparkles, Zap, ArrowUpRight,
  Calendar, Pause, Gift, CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui';
import {
  trackAppShared,
  trackBugReported,
  trackHelpRequested,
  trackLanguageChanged,
  trackUpgradeBannerClicked,
} from '@/services/analyticsService';
import {
  trackAmpCreditsClicked,
  trackAmpSubscriptionClicked,
  trackAmpOurPlansClicked,
  trackAmpManageSubscriptionClicked,
  trackAmpDeleteAccountClicked,
  trackAmpSocialLinkClicked,
  trackAmpLegalLinkClicked,
  trackAmpCancelFlowStepViewed,
  trackAmpCancelFlowExited,
  trackAmpRetentionOfferAccepted,
  trackAmpRetentionOfferDeclined,
  trackAmpAnnualPushViewed,
  trackAmpAnnualPushClicked,
  trackAmpAnnualPushDismissed,
  trackAmpSwitchToYearlyClicked,
} from '@/services/amplitudeService';
import { useDirectPayment } from '@/components/shared/EmbeddedCheckoutModal';
import { HelpPopup } from '@/components/shared/HelpPopup';

type ManageView = 'menu' | 'cancel-1' | 'cancel-2' | 'cancel-pause' | 'cancel-freeweek' | 'cancel-yearlybonus' | 'cancel-3' | 'retention-success';
type CancelReason = string | null;

// Maps internal `ManageView` state to the public Amplitude `Cancel Flow Step
// Viewed` taxonomy. Returns undefined for views that aren't a cancel step
// (success screen has its own events).
type CancelFlowStep =
  | 'menu'
  | 'lose_benefits'
  | 'reason_select'
  | 'pause_offer'
  | 'free_week_offer'
  | 'yearly_bonus_offer'
  | 'contact_email';
function cancelViewToStep(view: ManageView): CancelFlowStep | undefined {
  switch (view) {
    case 'menu': return 'menu';
    case 'cancel-1': return 'lose_benefits';
    case 'cancel-2': return 'reason_select';
    case 'cancel-pause': return 'pause_offer';
    case 'cancel-freeweek': return 'free_week_offer';
    case 'cancel-yearlybonus': return 'yearly_bonus_offer';
    case 'cancel-3': return 'contact_email';
    default: return undefined;
  }
}

export function AccountPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { firebaseUser, signOut } = useAuth();
  const { user } = useUserStore();
  const { language, setLanguage } = useSettingsStore();
  
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [manageView, setManageView] = useState<ManageView>('menu');
  const [cancelReason, setCancelReason] = useState<CancelReason>(null);
  const [retentionLoading, setRetentionLoading] = useState(false);
  const [retentionSuccess, setRetentionSuccess] = useState<string | null>(null);
  const [noSubToast, setNoSubToast] = useState(false);
  const { openPayment, paymentModal } = useDirectPayment();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showHelpPopup, setShowHelpPopup] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const handleDeleteAccount = async () => {
    if (!user?.id) return;
    trackAmpDeleteAccountClicked({ stage: 'confirmed', user_plan: user?.userPlan });
    setDeleteLoading(true);
    try {
      await apiService.deleteUser(user.id);
      setShowDeleteConfirm(false);
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Failed to delete account:', error);
      setDeleteLoading(false);
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: `${t('appName')} - AI Video Generator`,
      text: `Check out ${t('appName')} - AI Video Generator! Create amazing videos with AI. Download now: https://vivideo.ai`,
      url: 'https://vivideo.ai',
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        trackAppShared();
      } else {
        await navigator.clipboard.writeText(shareData.url);
        trackAppShared();
        alert(t('success.linkCopied'));
      }
    } catch (err) {
      console.error('Error sharing:', err);
    }
  };
  
  const handleReportBug = () => {
    trackBugReported();
    const subject = encodeURIComponent(t('support.reportBug'));
    const body = encodeURIComponent(
      `App Version: ${APP_VERSION}\nUser: ${email}\n\nPlease describe the bug:\n\n`
    );
    window.location.href = `mailto:support@vivideo.ai?subject=${subject}&body=${body}`;
  };
  
  const displayName = user?.displayName || user?.name || firebaseUser?.displayName || t('account.guestUser');
  const email = firebaseUser?.email || user?.email || '';
  const avatarUrl = user?.profilePictureUrl || user?.avatarUrl || firebaseUser?.photoURL;
  const isPro = isPaidPlan(user?.userPlan);
  const credits = user?.remainingCredit ?? 0;
  const planName = user?.userPlan === 'max' ? t('subscription.max') : isPro ? t('subscription.pro') : t('subscription.free');
  const planCredits = user?.userPlan === 'max' ? 100 : 30;
  const isWeekly = user?.priceType === 'weekly';
  const isMonthly = user?.priceType === 'monthly';
  const isNonYearly = isWeekly || isMonthly;
  const currentPlanConfig = user?.userPlan === 'max' ? MAX_PLAN : PRO_PLAN;
  const retentionUsed = !!user?.retentionOfferUsed;
  const [annualPushDismissed, setAnnualPushDismissed] = useState(false);

  const currentLanguage = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];

  const billingPeriod = user?.priceType || 'none';
  const userPlanKey = user?.userPlan || 'free';

  const openManageModal = () => {
    setManageView('menu');
    setCancelReason(null);
    setRetentionSuccess(null);
    setShowManageModal(true);
  };
  const closeManageModal = () => {
    if (showManageModal) {
      const step = cancelViewToStep(manageView);
      if (step && step !== 'menu') {
        trackAmpCancelFlowExited({
          step,
          exit_action: 'close_modal',
          user_plan: userPlanKey,
        });
      }
    }
    setShowManageModal(false);
    setManageView('menu');
    setCancelReason(null);
    setRetentionSuccess(null);
  };

  // Emit `Cancel Flow Step Viewed` whenever the modal is open and the view
  // changes to a cancel-* step. Keeps instrumentation in one place so every
  // flow entry (cancel, retention offers, final email) is tracked.
  useEffect(() => {
    if (!showManageModal) return;
    const step = cancelViewToStep(manageView);
    if (!step) return;
    trackAmpCancelFlowStepViewed({
      step,
      user_plan: userPlanKey,
      billing_period: billingPeriod,
    });
  }, [showManageModal, manageView, userPlanKey, billingPeriod]);

  const handleRetentionAction = async (action: 'pause' | 'free_week' | 'yearly_bonus') => {
    if (!firebaseUser || retentionLoading) return;
    trackAmpRetentionOfferAccepted({
      offer: action,
      user_plan: userPlanKey,
      billing_period: billingPeriod,
    });
    setRetentionLoading(true);
    try {
      const { userId: freshUserId } = await apiService.getUserById(firebaseUser.email || '', firebaseUser.uid);
      await apiService.manageSubscription(freshUserId, action);
      setRetentionSuccess(action);
      setManageView('retention-success');
    } catch (err) { console.error('Retention action error:', err); }
    finally { setRetentionLoading(false); }
  };

  const handleSwitchToYearly = (source: 'annual_push' | 'cancel_flow' = 'annual_push') => {
    trackAmpSwitchToYearlyClicked({
      source,
      user_plan: userPlanKey,
      billing_period: billingPeriod,
    });
    openPayment({
      type: 'upgrade',
      label: t('account.cancelFlow.switchToYearly', { percent: currentPlanConfig.yearlySavingsPercent }),
      description: `$${currentPlanConfig.yearlyPerWeek}${t('subscription.perWeek')}`,
      amount: `$${currentPlanConfig.yearlyAmount}/${t('subscription.billingToggle.yearly').toLowerCase()}`,
      priceId: currentPlanConfig.yearlyPriceId,
    });
  };

  const dismissAnnualPush = () => {
    trackAmpAnnualPushDismissed({ user_plan: userPlanKey, billing_period: billingPeriod });
    setAnnualPushDismissed(true);
  };

  // Fire `Annual Push Viewed` once per mount when the push is actually visible,
  // so funnel reports can compare views to clicks/dismissals.
  useEffect(() => {
    if (isPro && isNonYearly && !annualPushDismissed) {
      trackAmpAnnualPushViewed({ user_plan: userPlanKey, billing_period: billingPeriod });
    }
    // Intentionally only on first render when eligibility is determined.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const CANCEL_LOSE_BENEFITS = [
    { icon: Zap, text: t('account.cancelFlow.loseBenefit1', { credits: planCredits }) },
    { icon: Film, text: t('account.cancelFlow.loseBenefit2') },
    { icon: Download, text: t('account.cancelFlow.loseBenefit3') },
    { icon: Sparkles, text: t('account.cancelFlow.loseBenefit4') },
    { icon: Clock, text: t('account.cancelFlow.loseBenefit5') },
  ];

  const CANCEL_REASONS = [
    { id: 'not_using', label: t('account.cancelFlow.reason1'), desc: t('account.cancelFlow.reason1Desc'), icon: Clock },
    { id: 'too_expensive', label: t('account.cancelFlow.reason2'), desc: t('account.cancelFlow.reason2Desc'), icon: DollarSign },
    { id: 'quality', label: t('account.cancelFlow.reason3'), desc: t('account.cancelFlow.reason3Desc'), icon: Star },
    { id: 'other', label: t('account.cancelFlow.reason4'), desc: t('account.cancelFlow.reason4Desc'), icon: HelpCircle },
  ];

  const renderManageModal = () => {
    if (!showManageModal) return null;

    const backTo = (view: ManageView) => () => { setManageView(view); setCancelReason(null); };

    return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center animate-fade-in">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={closeManageModal} />
        <div className="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto mx-4 rounded-2xl bg-surface border border-glass-border shadow-2xl">
          <button onClick={closeManageModal} className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-surface-light transition-colors z-20">
            <X className="w-5 h-5 text-text-tertiary" />
          </button>

          {manageView === 'menu' && (
            <div className="p-6 md:p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Settings className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-xl font-bold text-text-primary mb-1">{t('account.manageSubscription')}</h2>
                <p className="text-sm text-text-secondary">{planName}</p>
              </div>
              <div className="space-y-3 mb-6">
                <button
                  onClick={() => { closeManageModal(); navigate('/subscription'); }}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-glass-border bg-surface-light hover:border-primary/30 transition-all"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <ArrowUpRight className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-text-primary">{t('account.manage.upgradePlan')}</p>
                    <p className="text-xs text-text-tertiary">{t('account.manage.upgradeDesc')}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-text-tertiary -rotate-90 flex-shrink-0" />
                </button>
                <button
                  onClick={() => setManageView('cancel-1')}
                  className="w-full flex items-center gap-4 p-4 rounded-xl border border-glass-border bg-surface-light hover:border-red-500/20 transition-all"
                >
                  <div className="w-10 h-10 rounded-xl bg-red-500/10 flex items-center justify-center flex-shrink-0">
                    <XCircle className="w-5 h-5 text-red-400" />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="text-sm font-medium text-text-primary">{t('account.manage.cancelSubscription')}</p>
                  </div>
                  <ChevronDown className="w-4 h-4 text-text-tertiary -rotate-90 flex-shrink-0" />
                </button>
              </div>
            </div>
          )}

          {manageView === 'cancel-1' && (
            <div className="p-6 md:p-8">
              <button onClick={backTo('menu')} className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-5 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                <span>{t('common.goBack')}</span>
              </button>
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-amber-500" />
                </div>
                <h2 className="text-xl font-bold text-text-primary mb-1">{t('account.cancelFlow.step1Title')}</h2>
                <p className="text-sm text-text-secondary">{t('account.cancelFlow.step1Subtitle')}</p>
              </div>
              <div className="space-y-3 mb-8">
                {CANCEL_LOSE_BENEFITS.map((b, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-red-500/5 border border-red-500/10">
                    <b.icon className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <span className="text-sm text-text-primary">{b.text}</span>
                    <XCircle className="w-4 h-4 text-red-400/60 ml-auto flex-shrink-0" />
                  </div>
                ))}
              </div>
              <Button
                size="lg"
                className="w-full mb-3 bg-gradient-to-r from-primary to-accent hover:opacity-90"
                onClick={() => {
                  trackAmpCancelFlowExited({
                    step: 'lose_benefits',
                    exit_action: 'keep_subscription',
                    user_plan: userPlanKey,
                  });
                  closeManageModal();
                }}
              >
                {t('account.cancelFlow.keepSubscription')}
              </Button>
              <button
                onClick={() => setManageView('cancel-2')}
                className="w-full py-2.5 text-sm text-text-tertiary hover:text-text-secondary transition-colors"
              >
                {t('account.cancelFlow.stillWantCancel')}
              </button>
            </div>
          )}

          {manageView === 'cancel-2' && (
            <div className="p-6 md:p-8">
              <button onClick={backTo('cancel-1')} className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-5 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                <span>{t('common.goBack')}</span>
              </button>
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-text-primary mb-1">{t('account.cancelFlow.step2Title')}</h2>
                <p className="text-sm text-text-secondary">{t('account.cancelFlow.step2Subtitle')}</p>
              </div>
              <div className="space-y-3 mb-6">
                {CANCEL_REASONS.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setCancelReason(r.id)}
                    className={cn(
                      "w-full text-left p-4 rounded-xl border transition-all",
                      cancelReason === r.id
                        ? "border-primary/50 bg-primary/5"
                        : "border-glass-border bg-surface-light hover:border-primary/30"
                    )}
                  >
                    <div className="flex items-center gap-3 mb-1">
                      <r.icon className="w-5 h-5 text-text-secondary flex-shrink-0" />
                      <span className="text-sm font-medium text-text-primary">{r.label}</span>
                    </div>
                    {cancelReason === r.id && (
                      <p className="text-xs text-text-secondary mt-2 ml-8 leading-relaxed animate-fade-in">{r.desc}</p>
                    )}
                  </button>
                ))}
              </div>
              {cancelReason && (
                <div className="animate-fade-in space-y-3">
                  {(cancelReason === 'too_expensive' || cancelReason === 'not_using') && isNonYearly && (
                    <button
                      onClick={() => handleSwitchToYearly('cancel_flow')}
                      className="w-full p-4 rounded-xl border-2 border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-500/50 transition-all text-left"
                    >
                      <div className="flex items-center gap-3 mb-1">
                        <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0">
                          <Calendar className="w-5 h-5 text-emerald-500" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-emerald-500">
                            {t('account.cancelFlow.switchToYearly', { percent: currentPlanConfig.yearlySavingsPercent })}
                          </p>
                          <p className="text-xs text-text-secondary mt-0.5">
                            {t('account.cancelFlow.switchToYearlyDesc', { price: currentPlanConfig.yearlyPerWeek, currentPrice: currentPlanConfig.weeklyAmount })}
                          </p>
                        </div>
                      </div>
                    </button>
                  )}
                  {cancelReason === 'not_using' && (
                    <Button size="lg" className="w-full bg-primary hover:bg-primary/90" onClick={() => { closeManageModal(); navigate('/'); }}>
                      <Sparkles className="w-4 h-4 mr-2" />
                      {t('account.cancelFlow.goCreateVideo')}
                    </Button>
                  )}
                  {cancelReason !== 'not_using' && (
                    <Button size="lg" className="w-full bg-primary hover:bg-primary/90" onClick={() => {
                      const subject = encodeURIComponent(t('support.help'));
                      const body = encodeURIComponent(`Hi, I need help with my subscription.\n\nEmail: ${email}\nPlan: ${planName}\nIssue: ${CANCEL_REASONS.find(r => r.id === cancelReason)?.label || ''}\n\n`);
                      window.location.href = `mailto:support@vivideo.ai?subject=${subject}&body=${body}`;
                    }}>
                      <Mail className="w-4 h-4 mr-2" />
                      {t('account.cancelFlow.contactSupport')}
                    </Button>
                  )}
                  <button
                    onClick={() => setManageView(retentionUsed ? 'cancel-3' : isWeekly ? 'cancel-pause' : 'cancel-yearlybonus')}
                    className="w-full py-2.5 text-sm text-text-tertiary hover:text-text-secondary transition-colors"
                  >
                    {t('account.cancelFlow.continueCancellation')}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step: Pause offer (shown after "continue cancellation", skipped if already used) */}
          {manageView === 'cancel-pause' && (
            <div className="p-6 md:p-8">
              <button onClick={backTo('cancel-2')} className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-5 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                <span>{t('common.goBack')}</span>
              </button>
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-4">
                  <Pause className="w-8 h-8 text-blue-500" />
                </div>
                <h2 className="text-xl font-bold text-text-primary mb-2">{t('account.cancelFlow.pauseTitle')}</h2>
                <p className="text-sm text-text-secondary leading-relaxed">{t('account.cancelFlow.pauseDesc')}</p>
              </div>
              <Button
                size="lg"
                className="w-full mb-3 bg-blue-500 hover:bg-blue-600"
                onClick={() => handleRetentionAction('pause')}
                isLoading={retentionLoading}
              >
                <Pause className="w-4 h-4 mr-2" />
                {t('account.cancelFlow.pauseTitle')}
              </Button>
              <button
                onClick={() => {
                  trackAmpRetentionOfferDeclined({
                    offer: 'pause',
                    user_plan: userPlanKey,
                    billing_period: billingPeriod,
                  });
                  setManageView(isWeekly ? 'cancel-freeweek' : 'cancel-3');
                }}
                className="w-full py-2.5 text-sm text-text-tertiary hover:text-text-secondary transition-colors"
              >
                {t('account.cancelFlow.continueCancellation')}
              </button>
            </div>
          )}

          {/* Step: Free week offer (weekly subs only, shown after declining pause) */}
          {manageView === 'cancel-freeweek' && (
            <div className="p-6 md:p-8">
              <button onClick={backTo('cancel-pause')} className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-5 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                <span>{t('common.goBack')}</span>
              </button>
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
                  <Gift className="w-8 h-8 text-amber-500" />
                </div>
                <h2 className="text-xl font-bold text-text-primary mb-2">{t('account.cancelFlow.freeWeekTitle')}</h2>
                <p className="text-sm text-text-secondary leading-relaxed">{t('account.cancelFlow.freeWeekDesc')}</p>
              </div>
              <Button
                size="lg"
                className="w-full mb-3 bg-amber-500 hover:bg-amber-600"
                onClick={() => handleRetentionAction('free_week')}
                isLoading={retentionLoading}
              >
                <Gift className="w-4 h-4 mr-2" />
                {t('account.cancelFlow.freeWeekTitle')}
              </Button>
              <button
                onClick={() => {
                  trackAmpRetentionOfferDeclined({
                    offer: 'free_week',
                    user_plan: userPlanKey,
                    billing_period: billingPeriod,
                  });
                  setManageView('cancel-3');
                }}
                className="w-full py-2.5 text-sm text-text-tertiary hover:text-text-secondary transition-colors"
              >
                {t('account.cancelFlow.continueCancellation')}
              </button>
            </div>
          )}

          {/* Step: Yearly bonus offer (yearly users only) */}
          {manageView === 'cancel-yearlybonus' && (
            <div className="p-6 md:p-8">
              <button onClick={backTo('cancel-2')} className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-5 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                <span>{t('common.goBack')}</span>
              </button>
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
                  <Gift className="w-8 h-8 text-amber-500" />
                </div>
                <h2 className="text-xl font-bold text-text-primary mb-2">{t('account.cancelFlow.yearlyBonusTitle')}</h2>
                <p className="text-sm text-text-secondary leading-relaxed">{t('account.cancelFlow.yearlyBonusDesc')}</p>
              </div>
              <Button
                size="lg"
                className="w-full mb-3 bg-amber-500 hover:bg-amber-600"
                onClick={() => handleRetentionAction('yearly_bonus')}
                isLoading={retentionLoading}
              >
                <Gift className="w-4 h-4 mr-2" />
                {t('account.cancelFlow.yearlyBonusTitle')}
              </Button>
              <button
                onClick={() => {
                  trackAmpRetentionOfferDeclined({
                    offer: 'yearly_bonus',
                    user_plan: userPlanKey,
                    billing_period: billingPeriod,
                  });
                  setManageView('cancel-3');
                }}
                className="w-full py-2.5 text-sm text-text-tertiary hover:text-text-secondary transition-colors"
              >
                {t('account.cancelFlow.continueCancellation')}
              </button>
            </div>
          )}

          {/* Retention success screen */}
          {manageView === 'retention-success' && (
            <div className="p-6 md:p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-500" />
              </div>
              <h2 className="text-xl font-bold text-text-primary mb-2">
                {retentionSuccess === 'pause' ? t('account.cancelFlow.pauseSuccessTitle') : retentionSuccess === 'free_week' ? t('account.cancelFlow.freeWeekSuccessTitle') : t('account.cancelFlow.yearlyBonusSuccessTitle')}
              </h2>
              <p className="text-sm text-text-secondary mb-6">
                {retentionSuccess === 'pause' ? t('account.cancelFlow.pauseSuccessDesc') : retentionSuccess === 'free_week' ? t('account.cancelFlow.freeWeekSuccessDesc') : t('account.cancelFlow.yearlyBonusSuccessDesc')}
              </p>
              <Button size="lg" className="w-full bg-primary hover:bg-primary/90" onClick={closeManageModal}>
                {t('common.done')}
              </Button>
            </div>
          )}

          {manageView === 'cancel-3' && (
            <div className="p-6 md:p-8">
              <button onClick={backTo('cancel-2')} className="flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-5 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                <span>{t('common.goBack')}</span>
              </button>
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-surface-light flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-text-secondary" />
                </div>
                <h2 className="text-xl font-bold text-text-primary mb-1">{t('account.cancelFlow.step3Title')}</h2>
                <p className="text-sm text-text-secondary mb-4">{t('account.cancelFlow.step3Subtitle')}</p>
                <p className="text-base font-semibold text-primary select-none pointer-events-none" aria-hidden="true">support@vivideo.ai</p>
              </div>
              <div className="flex items-center justify-center gap-2 mt-2">
                <Clock className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                <p className="text-sm text-text-tertiary">{t('account.cancelFlow.step3ResponseTime')}</p>
              </div>
            </div>
          )}

        </div>
      </div>
    );
  };

  return (
    <div className="max-w-2xl mx-auto animate-fade-in pb-8">
      <h1 className="text-2xl font-bold text-text-primary mb-6">{t('settings.title')}</h1>

      {/* Profile Section */}
      <div className="flex flex-col items-center mb-8">
        {/* Profile Picture with Badge */}
        <div className="relative mb-4">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={displayName}
              className="w-20 h-20 rounded-full object-cover ring-2 ring-primary/30"
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-amber-700 flex items-center justify-center ring-2 ring-primary/30">
              <span className="text-3xl font-bold text-white">
                {displayName.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          {/* Plan Badge */}
          <div className={cn(
            "absolute -bottom-1.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full text-xs font-semibold",
            user?.userPlan === 'max'
              ? "bg-gradient-to-r from-primary to-accent text-white"
              : isPro 
                ? "bg-gradient-to-r from-purple-500 to-pink-500 text-white" 
                : "bg-surface-light border border-glass-border text-text-primary"
          )}>
            {planName}
          </div>
        </div>
        
        {/* Username */}
        <h2 className="text-lg font-semibold text-text-primary">{displayName}</h2>
        
        {/* Email */}
        <p className="text-sm text-text-secondary">{email}</p>
      </div>

      {/* Upgrade Banner (only for Free users) */}
      {!isPro && (
        <button
          onClick={() => {
            trackUpgradeBannerClicked('account_page');
            navigate('/subscription');
          }}
          className="w-full mb-6 p-4 rounded-2xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center gap-3 hover:opacity-90 transition-opacity"
        >
          <div className="p-2.5 bg-white/20 rounded-xl">
            <Crown className="w-6 h-6 text-white animate-pulse" />
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-bold text-white">{t('subscription.unlockUnlimited')}</p>
            <p className="text-xs text-white/70">{t('subscription.removeWatermarksPriority')}</p>
          </div>
          <ChevronDown className="w-4 h-4 text-white -rotate-90" />
        </button>
      )}

      {/* Account Section */}
      <div className="bg-surface rounded-2xl border border-glass-border p-6 mb-6">
        <h3 className="text-sm font-semibold text-text-secondary mb-4">{t('account.title').toUpperCase()}</h3>
        
        <div className="space-y-2">
          {/* Credits */}
          <button
            onClick={() => {
              trackAmpCreditsClicked({ credits, is_pro: isPro });
              navigate('/subscription');
            }}
            className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-surface-light/70 transition-colors"
          >
            <div className="flex items-center gap-4">
              <Clock className="w-6 h-6 text-text-primary" />
              <span className="text-base text-text-primary">{t('account.credits')}</span>
            </div>
            <span className="px-3 py-1 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold text-sm shadow-lg shadow-purple-500/40">
              {credits}
            </span>
          </button>

          {/* Subscription */}
          <button
            onClick={() => {
              trackAmpSubscriptionClicked({ user_plan: userPlanKey, is_pro: isPro });
              navigate('/subscription');
            }}
            className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-surface-light/70 transition-colors"
          >
            <div className="flex items-center gap-4">
              <Briefcase className="w-6 h-6 text-text-primary" />
              <span className="text-base text-text-primary">{t('account.subscription')}</span>
            </div>
            <span className={cn(
              "text-base font-semibold",
              isPro ? "text-purple-500" : "text-text-primary"
            )}>
              {planName}
            </span>
          </button>

          {/* Our Plans */}
          <button
            onClick={() => {
              trackAmpOurPlansClicked({ is_pro: isPro });
              navigate('/subscription');
            }}
            className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-surface-light/70 transition-colors"
          >
            <div className="flex items-center gap-4">
              <Star className="w-6 h-6 text-text-primary" />
              <span className="text-base text-text-primary">{t('account.ourPlans')}</span>
            </div>
            <ChevronDown className="w-5 h-5 text-text-tertiary -rotate-90" />
          </button>
        </div>
      </div>

      {/* Annual Plan Push (weekly subscribers only) */}
      {isPro && isNonYearly && !annualPushDismissed && (
        <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-500/10 to-green-500/5 p-5 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Calendar className="w-6 h-6 text-emerald-500" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-text-primary mb-1">
                {t('account.annualPush.title', { percent: currentPlanConfig.yearlySavingsPercent })}
              </h3>
              <p className="text-xs text-text-secondary leading-relaxed mb-3">
                {t('account.annualPush.subtitle', {
                  weeklyPrice: currentPlanConfig.weeklyAmount,
                  yearlyPrice: currentPlanConfig.yearlyPerWeek,
                  savings: currentPlanConfig.yearlySavings,
                })}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  className="bg-emerald-500 hover:bg-emerald-600 text-white text-xs px-4"
                  onClick={() => {
                    trackAmpAnnualPushClicked({ user_plan: userPlanKey, billing_period: billingPeriod });
                    handleSwitchToYearly('annual_push');
                  }}
                >
                  {t('account.annualPush.cta')}
                </Button>
                <button onClick={dismissAnnualPush} className="text-xs text-text-tertiary hover:text-text-secondary transition-colors px-2 py-1">
                  {t('account.annualPush.dismiss')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Section */}
      <div className="bg-surface rounded-2xl border border-glass-border p-6 mb-6">
        <h3 className="text-sm font-semibold text-text-secondary mb-4">{t('settings.title').toUpperCase()}</h3>
        
        <div className="space-y-2">
          {/* Language */}
          <div className="relative">
            <button
              onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-surface-light/70 transition-colors"
            >
              <div className="flex items-center gap-4">
                <Globe className="w-6 h-6 text-text-primary" />
                <span className="text-base text-text-primary">{t('settings.language')}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-lg">{currentLanguage.flag}</span>
                <ChevronDown className={cn(
                  "w-5 h-5 text-text-tertiary transition-transform",
                  showLanguageDropdown && "rotate-180"
                )} />
              </div>
            </button>
            
            {showLanguageDropdown && (
              <div className="absolute right-0 top-full mt-2 w-48 py-2 bg-surface border border-glass-border rounded-xl shadow-xl z-50 max-h-64 overflow-y-auto">
                {LANGUAGES.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => {
                      setLanguage(lang.code as Language);
                      trackLanguageChanged(lang.code);
                      setShowLanguageDropdown(false);
                    }}
                    className={cn(
                      "w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-surface-light transition-colors",
                      language === lang.code && "bg-primary/10 text-primary"
                    )}
                  >
                    <span className="text-lg">{lang.flag}</span>
                    <span className="text-sm">{lang.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Social Section */}
      <div className="bg-surface rounded-2xl border border-glass-border p-6 mb-6">
        <h3 className="text-sm font-semibold text-text-secondary mb-4">{t('social.title').toUpperCase()}</h3>
        
        <div className="space-y-2">
          {/* Visit Website */}
          <a
            href="https://vivideo.ai"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAmpSocialLinkClicked('website')}
            className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-surface-light/70 transition-colors"
          >
            <div className="flex items-center gap-4">
              <Globe className="w-6 h-6 text-text-primary" />
              <span className="text-base text-text-primary">{t('social.visitWebsite')}</span>
            </div>
            <ChevronDown className="w-5 h-5 text-text-tertiary -rotate-90" />
          </a>

          {/* Instagram */}
          <a
            href="https://www.instagram.com/vivideo.ai/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAmpSocialLinkClicked('instagram')}
            className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-surface-light/70 transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="w-6 h-6 rounded-md bg-gradient-to-br from-purple-500 via-pink-500 to-orange-500 flex items-center justify-center">
                <Instagram className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-base text-text-primary">{t('social.followInstagram')}</span>
            </div>
            <ChevronDown className="w-5 h-5 text-text-tertiary -rotate-90" />
          </a>

          {/* Facebook */}
          <a
            href="https://www.facebook.com/profile.php?id=61585145055158"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAmpSocialLinkClicked('facebook')}
            className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-surface-light/70 transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="w-6 h-6 rounded-md bg-[#1877F2] flex items-center justify-center">
                <Facebook className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-base text-text-primary">{t('social.followFacebook')}</span>
            </div>
            <ChevronDown className="w-5 h-5 text-text-tertiary -rotate-90" />
          </a>

          {/* LinkedIn */}
          <a
            href="https://www.linkedin.com/company/ai-video-generator-vivideo"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAmpSocialLinkClicked('linkedin')}
            className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-surface-light/70 transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="w-6 h-6 rounded-md bg-[#0077B5] flex items-center justify-center">
                <Linkedin className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-base text-text-primary">{t('social.followLinkedin')}</span>
            </div>
            <ChevronDown className="w-5 h-5 text-text-tertiary -rotate-90" />
          </a>

          {/* X (Twitter) */}
          <a
            href="https://x.com/vivideoai"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAmpSocialLinkClicked('x')}
            className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-surface-light/70 transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="w-6 h-6 rounded-md bg-text-primary flex items-center justify-center">
                <span className="text-background font-bold text-xs">𝕏</span>
              </div>
              <span className="text-base text-text-primary">{t('social.followX')}</span>
            </div>
            <ChevronDown className="w-5 h-5 text-text-tertiary -rotate-90" />
          </a>

          {/* YouTube */}
          <a
            href="https://www.youtube.com/@VivideoAI"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAmpSocialLinkClicked('youtube')}
            className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-surface-light/70 transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="w-6 h-6 rounded-md bg-[#FF0000] flex items-center justify-center">
                <Youtube className="w-3.5 h-3.5 text-white" />
              </div>
              <span className="text-base text-text-primary">{t('social.followYoutube')}</span>
            </div>
            <ChevronDown className="w-5 h-5 text-text-tertiary -rotate-90" />
          </a>
        </div>
      </div>

      {/* About Section */}
      <div className="bg-surface rounded-2xl border border-glass-border p-6 mb-6">
        <h3 className="text-sm font-semibold text-text-secondary mb-4">{t('about.title').toUpperCase()}</h3>
        
        <div className="space-y-2">
          {/* Invite Friends */}
          <button
            onClick={handleShare}
            className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-surface-light/70 transition-colors"
          >
            <div className="flex items-center gap-4">
              <Share2 className="w-6 h-6 text-text-primary" />
              <span className="text-base text-text-primary">{t('social.inviteFriends')}</span>
            </div>
            <ChevronDown className="w-5 h-5 text-text-tertiary -rotate-90" />
          </button>

          {/* About Us */}
          <a
            href="https://vivideo.ai"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAmpLegalLinkClicked('about')}
            className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-surface-light/70 transition-colors"
          >
            <div className="flex items-center gap-4">
              <Info className="w-6 h-6 text-text-primary" />
              <span className="text-base text-text-primary">{t('about.aboutUs')}</span>
            </div>
            <ChevronDown className="w-5 h-5 text-text-tertiary -rotate-90" />
          </a>

          {/* Manage Subscription */}
          <button
            onClick={() => {
              trackAmpManageSubscriptionClicked({
                user_plan: userPlanKey,
                is_pro: isPro,
                has_active_sub: isPro,
              });
              if (isPro) { openManageModal(); }
              else { setNoSubToast(true); setTimeout(() => setNoSubToast(false), 3000); }
            }}
            className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-surface-light/70 transition-colors"
          >
            <div className="flex items-center gap-4">
              <Settings className="w-6 h-6 text-text-primary" />
              <span className="text-base text-text-primary">{t('account.manageSubscription')}</span>
            </div>
            <ChevronDown className="w-5 h-5 text-text-tertiary -rotate-90" />
          </button>

          {/* Privacy Policy */}
          <a
            href="https://vivideo.ai/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAmpLegalLinkClicked('privacy')}
            className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-surface-light/70 transition-colors"
          >
            <div className="flex items-center gap-4">
              <Shield className="w-6 h-6 text-text-primary" />
              <span className="text-base text-text-primary">{t('about.privacyPolicy')}</span>
            </div>
            <ChevronDown className="w-5 h-5 text-text-tertiary -rotate-90" />
          </a>

          {/* Terms of Service */}
          <a
            href="https://vivideo.ai/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackAmpLegalLinkClicked('terms')}
            className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-surface-light/70 transition-colors"
          >
            <div className="flex items-center gap-4">
              <FileText className="w-6 h-6 text-text-primary" />
              <span className="text-base text-text-primary">{t('about.termsOfService')}</span>
            </div>
            <ChevronDown className="w-5 h-5 text-text-tertiary -rotate-90" />
          </a>
        </div>
      </div>

      {/* Help Section */}
      <div className="bg-surface rounded-2xl border border-glass-border p-6 mb-6">
        <h3 className="text-sm font-semibold text-text-secondary mb-4">{t('support.help').toUpperCase()}</h3>

        <div className="space-y-2">
          {/* Report a Bug */}
          <button
            onClick={handleReportBug}
            className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-surface-light/70 transition-colors"
          >
            <div className="flex items-center gap-4">
              <Bug className="w-6 h-6 text-text-primary" />
              <span className="text-base text-text-primary">{t('support.reportBug')}</span>
            </div>
            <ChevronDown className="w-5 h-5 text-text-tertiary -rotate-90" />
          </button>

          {/* Help */}
          <button
            onClick={() => {
              trackHelpRequested();
              setShowHelpPopup(true);
            }}
            className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-surface-light/70 transition-colors"
          >
            <div className="flex items-center gap-4">
              <HelpCircle className="w-6 h-6 text-text-primary" />
              <span className="text-base text-text-primary">{t('support.help')}</span>
            </div>
            <ChevronDown className="w-5 h-5 text-text-tertiary -rotate-90" />
          </button>
        </div>
      </div>

      {/* More Settings Section */}
      <div className="bg-surface rounded-2xl border border-glass-border mb-6 overflow-hidden">
        <div className="w-full flex items-center justify-between p-6">
          <h3 className="text-sm font-semibold text-text-secondary">{t('misc.more').toUpperCase()}</h3>
        </div>

        <div className="px-6 pb-6 space-y-2">
            {/* Version */}
            <div className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light">
              <div className="flex items-center gap-4">
                <Package className="w-6 h-6 text-text-primary" />
                <span className="text-base text-text-primary">{t('settings.version')}</span>
              </div>
              <span className="text-sm text-text-secondary">{APP_VERSION}</span>
            </div>

            {/* Delete Account */}
            <button
              onClick={() => {
                trackAmpDeleteAccountClicked({ stage: 'prompt', user_plan: user?.userPlan });
                setShowDeleteConfirm(true);
              }}
              className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-red-500/10 transition-colors"
            >
              <div className="flex items-center gap-4">
                <AlertTriangle className="w-6 h-6 text-red-500" />
                <span className="text-base text-red-500">{t('account.deleteAccount')}</span>
              </div>
              <ChevronDown className="w-5 h-5 text-text-tertiary -rotate-90" />
            </button>

            {/* Sign Out */}
            <button
              onClick={handleSignOut}
              className="w-full flex items-center justify-between p-4 rounded-xl bg-surface-light hover:bg-surface-light/70 transition-colors"
            >
              <div className="flex items-center gap-4">
                <LogOut className="w-6 h-6 text-text-primary" />
                <span className="text-base text-text-primary">{t('common.signOut')}</span>
              </div>
              <ChevronDown className="w-5 h-5 text-text-tertiary -rotate-90" />
            </button>
          </div>
      </div>
      
      {/* Click outside to close dropdown */}
      {showLanguageDropdown && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowLanguageDropdown(false)}
        />
      )}

      {renderManageModal()}
      {paymentModal}
      <HelpPopup open={showHelpPopup} onClose={() => setShowHelpPopup(false)} />
      {noSubToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[80] px-5 py-3 rounded-xl bg-surface border border-glass-border shadow-xl animate-fade-in">
          <p className="text-sm text-text-secondary">{t('account.noActiveSubscription')}</p>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-surface rounded-2xl border border-glass-border shadow-xl max-w-sm w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary">{t('account.deleteAccount')}</h3>
            </div>
            <p className="text-sm text-text-secondary mb-4">
              {t('account.deleteAccountWarning')}
            </p>
            <div className="mb-6 flex items-start gap-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 px-3.5 py-3">
              <Info className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-200/90 leading-relaxed">{t('account.deleteSubscriptionNotice')}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => {
                  trackAmpDeleteAccountClicked({ stage: 'canceled', user_plan: user?.userPlan });
                  setShowDeleteConfirm(false);
                }}
                disabled={deleteLoading}
                className="flex-1 px-4 py-3 rounded-xl bg-surface-light hover:bg-surface-light/70 text-text-primary text-sm font-medium transition-colors disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteLoading}
                className="flex-1 px-4 py-3 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-medium transition-colors disabled:opacity-50"
              >
                {deleteLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {t('common.loading')}
                  </span>
                ) : (
                  t('common.delete')
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
